import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import type {
  MarketingRequest,
  MarketingRequestStatus,
  MarketingRequestType,
} from '../types/marketing';
import {
  subscribeMarketingRequests,
  createMarketingRequest,
  updateMarketingRequest,
  deleteMarketingRequest,
} from '../utils/marketingRequestsService';
import {
  canCreateMarketingRequest,
  canAccessMarketingRequests,
} from '../utils/marketingRequestAccess';
import MarketingRequestSidebar from '../components/marketing/MarketingRequestSidebar';
import MarketingRequestTable from '../components/marketing/MarketingRequestTable';
import MarketingRequestCommentsPopout from '../components/marketing/MarketingRequestCommentsPopout';
import NewMarketingRequestModal, {
  type NewMarketingRequestFormValues,
} from '../components/marketing/NewMarketingRequestModal';
import { Navigate } from 'react-router-dom';

const CLOSED_STATUSES: MarketingRequestStatus[] = ['done', 'cancelled'];

const MarketingRequestsPage: React.FC = () => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const [searchParams, setSearchParams] = useSearchParams();

  const [requests, setRequests] = useState<MarketingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [listenError, setListenError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<MarketingRequestType | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<MarketingRequestStatus | null>(null);
  const [hideClosed, setHideClosed] = useState(true);
  const [popoutRequest, setPopoutRequest] = useState<MarketingRequest | null>(null);
  const [highlightCommentId, setHighlightCommentId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const me = useMemo(
    () => crmUsers.find(u => u.uid === user?.id),
    [crmUsers, user?.id],
  );

  const canCreate = canCreateMarketingRequest({
    authRole: user?.role,
    designations: me?.designations,
  });

  const hasAccess = canAccessMarketingRequests(user?.role, user?.designations);

  useEffect(() => {
    if (!hasAccess) return;
    const unsub = subscribeMarketingRequests(
      rows => {
        setRequests(rows);
        setLoading(false);
        setListenError(null);
      },
      err => {
        setLoading(false);
        const msg = err instanceof Error ? err.message : String(err);
        setListenError(msg);
      },
    );
    return unsub;
  }, [hasAccess]);

  /** Deep link: /marketing-hub/content-requests?request=<id>&comment=<commentId> */
  useEffect(() => {
    const requestId = searchParams.get('request');
    const commentId = searchParams.get('comment');
    if (!requestId) return;
    const r = requests.find(x => x.id === requestId);
    if (!r) return;
    setPopoutRequest(r);
    if (commentId) setHighlightCommentId(commentId);
    setSearchParams({}, { replace: true });
  }, [searchParams, requests, setSearchParams]);

  const filteredRequests = useMemo(() => {
    let result = requests;
    if (hideClosed) {
      result = result.filter(r => !CLOSED_STATUSES.includes(r.status));
    }
    if (selectedType) {
      result = result.filter(r => r.requestType === selectedType);
    }
    if (selectedStatus) {
      result = result.filter(r => r.status === selectedStatus);
    }
    return result;
  }, [requests, hideClosed, selectedType, selectedStatus]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { all: requests.length };
    for (const r of requests) {
      counts[r.requestType] = (counts[r.requestType] || 0) + 1;
    }
    return counts;
  }, [requests]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: requests.length };
    for (const r of requests) {
      counts[r.status] = (counts[r.status] || 0) + 1;
    }
    return counts;
  }, [requests]);

  const stats = useMemo(() => {
    const open = requests.filter(r => r.status === 'open' || r.status === 'in_progress').length;
    const waiting = requests.filter(r => r.status === 'waiting').length;
    return { shown: filteredRequests.length, open, waiting };
  }, [requests, filteredRequests.length]);

  const freshPopout = useMemo(() => {
    if (!popoutRequest) return null;
    return requests.find(r => r.id === popoutRequest.id) ?? popoutRequest;
  }, [popoutRequest, requests]);

  const handleCreate = useCallback(
    async (values: NewMarketingRequestFormValues) => {
      if (!user) return;
      setSaving(true);
      setCreateError(null);
      try {
        const displayName =
          me?.displayName || user.name || user.email || 'Unknown';
        await createMarketingRequest({
          title: values.title,
          description: values.description || null,
          requestType: values.requestType,
          priority: values.priority,
          dueDate: values.dueDate ? new Date(values.dueDate + 'T12:00:00') : null,
          requestedBy: user.id,
          requestedByName: displayName,
          assigneeUserId: user.id,
        });
        setShowCreate(false);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Could not create request.';
        setCreateError(msg);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [user, me],
  );

  const handleUpdate = useCallback(async (id: string, patch: Parameters<typeof updateMarketingRequest>[1]) => {
    await updateMarketingRequest(id, patch);
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    await deleteMarketingRequest(id);
    setPopoutRequest(null);
  }, []);

  if (!hasAccess) {
    return <Navigate to="/marketing-hub" replace />;
  }

  if (loading) {
    return (
      <div className="marketing-hub-loading">
        <div className="marketing-hub-loading-bar" />
      </div>
    );
  }

  return (
    <>
      <div className="marketing-hub-layout marketing-hub-layout--subpage">
        <MarketingRequestSidebar
          selectedType={selectedType}
          selectedStatus={selectedStatus}
          onSelectType={setSelectedType}
          onSelectStatus={setSelectedStatus}
          typeCounts={typeCounts}
          statusCounts={statusCounts}
          hideClosed={hideClosed}
          onToggleHideClosed={() => setHideClosed(h => !h)}
          onNewRequest={() => setShowCreate(true)}
          canCreate={canCreate}
        />

        <div className="marketing-hub-main">
          <div className="marketing-hub-toolbar">
            <div className="marketing-hub-toolbar-left">
              <h2 className="marketing-hub-title">Content Requests</h2>
              <div className="marketing-hub-stats">
                <span className="mh-stat">{stats.shown} shown</span>
                {stats.open > 0 && (
                  <span className="mh-stat mh-stat-active">{stats.open} active</span>
                )}
                {stats.waiting > 0 && (
                  <span className="mh-stat">{stats.waiting} waiting</span>
                )}
              </div>
            </div>
            {canCreate && (
              <div className="marketing-hub-toolbar-right">
                <button
                  type="button"
                  className="mh-btn-primary mh-toolbar-new"
                  onClick={() => setShowCreate(true)}
                >
                  New request
                </button>
              </div>
            )}
          </div>

          {listenError && (
            <div className="mh-requests-error" role="alert">
              Could not load content requests: {listenError}
            </div>
          )}

          <MarketingRequestTable
            requests={filteredRequests}
            onSelectRequest={setPopoutRequest}
            emptyMessage={
              canCreate
                ? 'No content requests yet. Create one to get started.'
                : 'No content requests match your filters.'
            }
          />
        </div>
      </div>

      <MarketingRequestCommentsPopout
        request={freshPopout}
        highlightCommentId={highlightCommentId}
        onHighlightCommentConsumed={() => setHighlightCommentId(null)}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onClose={() => setPopoutRequest(null)}
        canDelete={user?.role === 'admin'}
      />

      <NewMarketingRequestModal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setCreateError(null);
        }}
        onSubmit={handleCreate}
        saving={saving}
        error={createError}
      />
    </>
  );
};

export default MarketingRequestsPage;
